import React, { useReducer, useEffect, useState } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import NewIntroPage from './pages/NewIntroPage/NewIntroPage';
import DetailsPage from './pages/DetailsPage/DetailsPage';
import CardsPage from './pages/CardsPage/CardsPage';
import { CATEGORIES, getNormalizedFilters, PERMISSION_LEVEL_ENUM } from "./App.helpers";
import './App.scss';
import http from "./core/http";
import { capitalizeFirstLetter } from "./core/helpers";
import 'url-search-params-polyfill';
import './core/poly';

export const CARDS_PAGE_PATH = '';

const replaceData = (oldData, newData) => newData;

export const AppContext = React.createContext({});

function App() {
    const [data, setData] = useReducer(replaceData, { segments: [], occasions: [], triggers: [], drivers: [] });
    const [filters, setFilters] = useReducer(replaceData, []);
    const [credentials, setCredentials] = useReducer(replaceData, { permissionLevel: PERMISSION_LEVEL_ENUM.NOT_SET });
    const [userName, setUserName] = useState('Guest');
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isAuthRequested, setIsAuthRequested] = useState(false);
    const [isLoginModalActive, setIsLoginModalActive] = useState(false);
    const [isLoginFailed, setIsLoginFailed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoginFailedMsgShown, setIsLoginFailedMsgShown] = useState(false);
    const [isMenuExpanded, setIsMenuExpanded] = useState(false);
    const [isCardsPageBeenShown, setIsCardsPageBeenShown] = useState(false);
    const [isSubscribersPopupOpened, setIsSubscribersPopupOpened] = useState(false);
    const [isNDAPopupOpened, setIsNDAPopupOpened] = useState(false);
    const [isHelpPopupOpened, setIsHelpPopupOpened] = useState(false);

    useEffect(() => {
        if (isCardsPageBeenShown) {
            setTimeout(() => setIsMenuExpanded(true), 100);
            setTimeout(() => setIsMenuExpanded(false), 3000);
        }
    }, [isCardsPageBeenShown]);

    useEffect(() => {
        setIsLoginFailedMsgShown(isLoginFailed);
    }, [isLoginFailed]);

    function reFetchCredentials() {
        http.auth('access/get-credentials').then((newCredentials) => {
            const isUserAuthorized = !!newCredentials.ifma;
            const isNDAAccepted = newCredentials['accept-nda'];
            let permissionLevel = PERMISSION_LEVEL_ENUM.GUEST;

            if (isNDAAccepted) {
                setIsAuthorized(isUserAuthorized);
            }
            if (isUserAuthorized) {
                if (!isNDAAccepted) {
                    setIsNDAPopupOpened(true);
                }
                permissionLevel = PERMISSION_LEVEL_ENUM.REGULAR;
            }
            if (Object.keys(newCredentials).includes('paying-subscriber')) {
                permissionLevel = PERMISSION_LEVEL_ENUM.SUBSCRIPTION;
            }
            setCredentials({
                ...newCredentials,
                permissionLevel,
            });
            setIsAuthRequested(true);
        })
    }

    useEffect(reFetchCredentials, []);

    useEffect(() => {
        if (isAuthorized) {
            http.auth('access/get-email').then((data) => {
                setUserName(data.value);
            });
        }
    }, [isAuthorized]);

    useEffect(() => {
        http.get('general/get')
            .then((data) => {
                handleGeneralData(data);
                preloadImages();
            });
        http.get('general/get-filter')
            .then((data) => {
                setFilters(getNormalizedFilters(data));
            });
    }, []);

    const handleGeneralData = (responseData) => {

        const categories = responseData.map(category => {
            return {
                ...category,
                subCategories: category.subCategories.map(subCategory => {
                    return {
                        ...subCategory,
                        name: capitalizeFirstLetter(subCategory.name),
                    }
                })
            }
        })

        setData({
            segments: categories[0].subCategories,
            occasions: categories[1].subCategories,
            triggers: categories[2].subCategories,
            drivers: categories[3].subCategories,
        });
    };

    const preloadImages = () => {
        /* preload the images */
        for (let i = 1; i <= 4; i++) {
            const img = new Image();
            img.src = `${process.env.REACT_APP_BASE_ROUTE}thumb-categories/${i}.png`;
        }
        for (let i = 1; i <= 145; i++) {
            const img1 = new Image();
            const img2 = new Image();
            img1.src = `${process.env.REACT_APP_BASE_ROUTE}thumb-list/${i}.jpeg`;
            img2.src = `${process.env.REACT_APP_BASE_ROUTE}thumb-single/${i}.jpeg`;
        }
    };

    const login = ({ email, password }) => {
        setIsLoading(true);
        http.auth(`authorization/form-login?username=${email}&password=${password}`)
            .then((data) => {
                setIsLoading(false);
                if (data.status === 'success') {
                    setIsLoginFailed(false);
                    reFetchCredentials();
                    setIsLoginModalActive(false);
                } else {
                    setIsLoginFailed(true);
                }
            })
            .catch(() => {
                setIsLoading(false);
                setIsLoginFailed(true);
            })
    };

    const handleDNAPopupClose = () => {
        window.location.href = process.env.REACT_APP_LOGOUT;
    };
    const handleDNAPopupAccept = () => {
        http.auth('access/set-nda-access').then(() => {
            setIsNDAPopupOpened(false);
            reFetchCredentials();
        });
    };

    return (
        <div className="App">
            <Router basename={process.env.REACT_APP_BASE_ROUTE}>
                <AppContext.Provider value={{
                    data,
                    filters,

                    login,
                    isLoading,
                    isLoginFailedMsgShown,
                    setIsLoginFailedMsgShown,
                    isLoginModalActive,
                    setIsLoginModalActive,

                    isMenuExpanded,
                    setIsCardsPageBeenShown,

                    userName,
                    credentials,
                    isAuthorized,

                    isSubscribersPopupOpened,
                    setIsSubscribersPopupOpened,

                    isNDAPopupOpened,
                    setIsNDAPopupOpened,
                    handleDNAPopupClose,
                    handleDNAPopupAccept,

                    isHelpPopupOpened,
                    setIsHelpPopupOpened,
                }}>
                    <div>
                        <Switch>
                            <Route
                                exact
                                path="/"
                                component={NewIntroPage}
                            />
                            <Route
                                path={`${CARDS_PAGE_PATH}/:name`}
                                exact
                                component={CardsPage}
                            />
                            {CATEGORIES.map(category => (
                                category.permissionLevel <= credentials.permissionLevel && (
                                    <Route
                                        key={category.id}
                                        path={`${CARDS_PAGE_PATH}/${category.path}/:id`}
                                        component={DetailsPage}
                                    />
                                )
                            ))}
                            {isAuthRequested && (
                                <Redirect to="/"/>
                            )}
                        </Switch>
                    </div>
                </AppContext.Provider>
            </Router>
        </div>
    );
}


export default App;
